import { Gender } from "api";
import { TFunction } from "i18next";
import { getAge } from "utils";
import * as yup from "yup";

const permissionToLeaveDefaultSchema = yup.object({
  canLeave: yup.boolean().notRequired(),
  guardianName: yup.string().notRequired(),
  guardianPhone: yup.string().notRequired(),
  personCollectingCandidate: yup.string().notRequired(),
});

const getPermissionToLeaveMinorSchema = (
  schema: typeof permissionToLeaveDefaultSchema,
  t: TFunction
) =>
  schema.shape({
    canLeave: yup
      .boolean()
      .required(t("details.permissionToLeave.canLeave.required")),
    guardianName: yup
      .string()
      .required(t("details.permissionToLeave.guardian.required"))
      .max(60, t("details.permissionToLeave.guardian.tooLong", { count: 60 })),
    guardianPhone: yup
      .string()
      .required(t("details.permissionToLeave.guardianPhone.required"))
      .matches(
        /^\+?[1-9][0-9\s-]{7,20}$/,
        t("details.permissionToLeave.guardianPhone.invalid")
      )
      .max(
        15,
        t("details.permissionToLeave.guardianPhone.tooLong", { count: 15 })
      ),
    personCollectingCandidate: yup
      .string()
      .notRequired()
      .max(
        60,
        t("details.permissionToLeave.personCollectingCandidate.tooLong", {
          count: 60,
        })
      ),
  });

const getPermissionToLeaveSuperMinorSchema = (
  schema: typeof permissionToLeaveDefaultSchema,
  t: TFunction
) =>
  getPermissionToLeaveMinorSchema(schema, t).shape({
    canLeave: yup
      .boolean()
      .isFalse(t("details.permissionToLeave.canLeave.mustBeFalse"))
      .required(t("details.permissionToLeave.canLeave.required")),
  });

export const getValidationSchema = (t: TFunction) =>
  yup.object({
    firstName: yup
      .string()
      .required(t("details.firstName.required"))
      .max(30, t("details.firstName.tooLong", { count: 30 })),
    middleName: yup
      .string()
      .max(30, t("details.secondName.tooLong", { count: 30 }))
      .default(""),
    lastName: yup
      .string()
      .required(t("details.lastName.required"))
      .max(30, t("details.lastName.tooLong", { count: 30 })),
    gender: yup
      .mixed<Gender>()
      .oneOf(Object.values(Gender) as Gender[])
      .required(t("details.gender.required")),
    dateOfBirth: yup.string().required(t("details.dateOfBirth.required")),

    emailAddress: yup
      .string()
      .email(t("details.email.invalid"))
      .required(t("details.email.required"))
      .max(64, t("details.email.tooLong", { count: 64 })),
    confirmEmailAddress: yup
      .string()
      .email(t("details.email.invalid"))
      .required(t("details.email.required"))
      .oneOf([yup.ref("emailAddress")], t("details.confirmEmail.mismatch"))
      .max(64, t("details.email.tooLong", { count: 64 })),
    phoneNumber: yup
      .string()
      .required(t("details.phone.required"))
      .matches(/^\+?[1-9][0-9\s-]{7,20}$/, t("details.phone.invalid"))
      .max(15, t("details.phone.tooLong", { count: 15 })),
    guardian: yup
      .string()
      .default("")
      .max(60, t("details.guardian.tooLong", { count: 60 })),
    address: yup.object({
      street: yup
        .string()
        .required(t("details.street.required"))
        .max(50, t("details.street.tooLong", { count: 50 })),
      postalCode: yup
        .string()
        .required(t("details.postalCode.required"))
        .matches(/^\d{2}-\d{3}$/, t("details.postalCode.invalid")),
      city: yup
        .string()
        .required(t("details.city.required"))
        .max(23, t("details.city.tooLong", { count: 23 })),
    }),
    permissionToLeave: permissionToLeaveDefaultSchema.when(
      ["dateOfBirth", "$examDate"],
      ([dateOfBirth, examDate], schema) => {
        return isSuperMinor(examDate, dateOfBirth)
          ? getPermissionToLeaveSuperMinorSchema(schema, t)
          : isMinor(examDate, dateOfBirth)
            ? getPermissionToLeaveMinorSchema(schema, t)
            : schema;
      }
    ),
    specialRequirements: yup.object({
      disability: yup.boolean().nullable(),
      dyslexia: yup.boolean().nullable(),
      description: yup
        .string()
        .max(500, t("details.specialRequirements.description.tooLong"))
        .when(["disability", "dyslexia"], {
          is: (disability: boolean, dyslexia: boolean) =>
            disability || dyslexia,
          then: (schema) =>
            schema.required(
              t("details.specialRequirements.description.required")
            ),
        }),
      files: yup
        .array()
        .of(
          yup
            .mixed<File>()
            .required()
            .test(
              "fileType",
              t("details.specialRequirements.files.error.fileType", {
                allowedTypes: "pdf, jpg, jpeg, png",
              }),
              (value) => {
                if (!value) return true;
                const file = value as File;
                const extension = file.name.split(".").pop()?.toLowerCase();
                return (
                  extension !== undefined &&
                  ["pdf", "jpg", "jpeg", "png"].includes(extension)
                );
              }
            )
            .test(
              "fileSize",
              t("details.specialRequirements.files.error.fileSize", {
                maxSize: "5MB",
              }),
              (value) => {
                if (!value) return true;
                const file = value as File;
                return file.size <= 5 * 1024 * 1024; // 5MB
              }
            )
        )
        .when(["disability", "dyslexia"], {
          is: (disability: boolean, dyslexia: boolean) =>
            disability || dyslexia,
          then: (schema) =>
            schema
              .required(t("details.specialRequirements.files.required"))
              .min(1, t("details.specialRequirements.files.required"))
              .max(5, t("details.specialRequirements.files.error.maxFiles")),
        }),
    }),
    marketingConsent: yup
      .boolean()
      .required(t("details.marketingConsent.required")),
  });

export type CandidateDetailsFormData = yup.InferType<
  ReturnType<typeof getValidationSchema>
>;

export const isMinor = (
  examDate?: Date | string,
  dateOfBirth?: Date | string
) => {
  const age = getAge(examDate, dateOfBirth);
  return age !== undefined && age < 18;
};

export const isSuperMinor = (
  examDate?: Date | string,
  dateOfBirth?: Date | string
) => {
  const age = getAge(examDate, dateOfBirth);
  return age !== undefined && age <= 11;
};
